import { useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';

/**
 * Wrapper around TanStack Query's useMutation hook
 * Provides a semantic API for committing changes (POST, PUT, PATCH, DELETE requests)
 *
 * @example
 * ```typescript
 * const teamService = new TeamService();
 * const createTeam = useCommit({
 *   ...teamService.createTeam(),
 *   onSuccess: (data) => // Team created: ${data}
 * });
 *
 * createTeam.mutate({ name: 'New Team' });
 * // or alias
 * createTeam.commit({ name: 'New Team' });
 * ```
 */
export function useCommit<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> & {
  commit: (variables: TVariables) => void;
} {
  const mutation = useMutation(options);

  return {
    ...mutation,
    commit: mutation.mutate, // Alias mutate to commit for semantic clarity
  };
}
